import { tagAs } from 'factions/metatagger'
import { SYLVANETH } from 'meta/factions'
import {
  COMBAT_PHASE,
  DURING_GAME,
  DURING_SETUP,
  END_OF_MOVEMENT_PHASE,
  END_OF_SETUP,
  END_OF_TURN,
  HERO_PHASE,
  MOVEMENT_PHASE,
  SHOOTING_PHASE,
  START_OF_HERO_PHASE,
  START_OF_ROUND,
  START_OF_SHOOTING_PHASE,
  START_OF_TURN,
  WARDS_PHASE,
} from 'types/phases'
import rule_sources from './rule_sources'
import { TItemDescriptions } from 'factions/factionTypes'

const BattleTraits = {
  [SYLVANETH]: {
    effects: [
      {
        name: `Endless Growth - Once Per Turn`,
        desc: `Declare: Pick each friendly Sylvaneth unit that is wholly within the creeping overgrowth to be the targets.
        Effect: For each target, roll a D3 and pick 1 of the following effects:
        Heal (X) the target, where X is equal to the roll.
        Return a number of slain models to it with a combined Health characteristic of up to the value of the roll.`,
        when: [END_OF_TURN],
      },
      {
        name: `The Creeping Overgrowth - Passive`,
        desc: `Effect: The creeping overgrowth is measured from each terrain feature that has a friendly overgrown token. The range of the creeping overgrowth is based on the number of friendly overgrown tokens on the battlefield as shown:
        0-2 - 6"
        3-5 - 9"
        6-8 - 12"
        9+ - Whole Battlefield`,
        when: [DURING_GAME],
      },
      {
        name: `Reclaimed By Nature - Once Per Turn`,
        desc: `Effect: If there are fewer than 3 friendly Awakened Wyldwoods on the battlefield, you can set up an Awakened Wyldwood on the battlefield more than 3" from all objectives and enemy units, more than 12" from all other friendly Awakened Wyldwoods and more than 1" from all other terrain features.`,
        when: [HERO_PHASE],
      },
      {
        name: `Creeping Dread - Once Per Turn`,
        desc: `Declare: Pick an enemy unit within the creeping overgrowth to be the target.
        Effect: For the rest of the turn, the first time your opponent declares a command for the target while it is not wholly within 12" of a Hero in their army, unless your opponent spends 1 additional command point to use that command, the command has no effect, it still counts as being used and the command points spent to use the command are still lost.`,
        when: [START_OF_TURN],
      },
      {
        name: `Command Ability: Fury of the Forest - Enemy Combat Phase`,
        desc: `Declare: Pick a friendly Sylvaneth unit wholly within the creeping overgrowth and that is in combat with only 1 enemy unit to use this ability.
        Effect: The unit using this ability has Strike-First for the rest of the turn.`,
        when: [COMBAT_PHASE],
      },
      {
        name: `Command Ability: The Land Awakens`,
        desc: `Declare: Pick a friendly non-Hero Revenant Infantry unit or friendly Dryads unit that has been destroyed to be the target.
        Effect: Set up a replacement unit with half the number of models from the target unit (rounding up) wholly within the creeping overgrowth and more than 9" from all enemy units.`,
        when: [END_OF_TURN],
      },
      {
        name: `Awakened Wyldwood (Faction Terrain) - Budding Forests - Passive`,
        desc: `Effect: When this terrain feature is set up, give it a friendly overgrown token.`,
        when: [DURING_GAME],
      },
      {
        name: `Awakened Wyldwood (Faction Terrain) - Ever Growing - Once Per Battle Round`,
        desc: `Declare: Pick the closest terrain feature to this terrain feature that does not have a friendly overgrown token to be the target.
        Effect: If the target is not controlled by your opponent, give the target a friendly overgrown token.`,
        when: [START_OF_ROUND],
      },
      {
        name: `Awakened Wyldwood (Faction Terrain) - Vengeful Forest Spirits - Once Per Turn`,
        desc: `Declare: Pick each enemy unit within 3" of this terrain feature to be the targets.
        Effect: Roll a D3 for each target. On a 2+, inflict an amount of mortal damage on the target equal to the roll.`,
        when: [COMBAT_PHASE],
      },
    ],
  },

  // Seasons of War
  /* 'The Burgeoning': {
    effects: [
      {
        name: `The Burgeoning`,
        desc: `Friendly SYLVANETH units that did not charge in the same turn and are wholly within 9" of an overgrown terrain feature or friendly Awakened Wyldwood have a ward of 6+.`,
        when: [WARDS_PHASE],
      },
    ],
  },
  'The Reaping': {
    effects: [
      {
        name: `The Reaping`,
        desc: `Add 3 to the range within which you can pick friendly SYLVANETH units with the Places of Power and From the Woodland Depths battle traits.`,
        when: [DURING_GAME],
      },
    ],
  },
  'The Dwindling': {
    effects: [
      {
        name: `The Dwindling`,
        desc: `In the hero phase, you can reroll 1 casting roll, 1 unbinding roll and 1 dispelling roll, so long as the friendly WIZARD you pick is a SYLVANETH WIZARD that is wholly within 9" of an overgrown terrain feature or friendly Awakened Wyldwood.`,
        when: [HERO_PHASE],
      },
    ],
  },
  Everdusk: {
    effects: [
      {
        name: `Everdusk`,
        desc: `Subtract 3" from the range within which you can pick friendly SYLVANETH units with the Places of Power and From the Woodland Depths battle traits. However, if the unmodified hit roll for an attack made with a melee weapon by a friendly SYLVANETH unit wholly within 6" of an overgrown terrain feature or friendly Awakened Wyldwood is 6, that attack scores 2 hits on the target instead of 1. Make a wound and save roll for each hit.`,
        when: [DURING_GAME, COMBAT_PHASE],
      },
    ],
  },

  'The Evergreen Hunt': {
    effects: [
      {
        name: `To Honour Kurnouth`,
        desc: `Friendly EVERGREEN HUNT units that do not have the HERO keyword have the Battleline battlefield role.`,
        when: [DURING_GAME],
      },
      {
        name: `Rhythm of the Chase`,
        desc: `After deployment, before the players have determined who will take the first turn, you can pick 1 enemy unit on the battlefield to be the quarry. If the quarry is destroyed, at the start of your next hero phase, you can pick 1 enemy unit on the battlefield to be the new quarry.`,
        when: [END_OF_SETUP],
      },
      {
        name: `Harmonies of the Hunt`,
        desc: `At the start of each battle round, after the priority roll has been made, each player commanding an Evergreen Hunt army must determine their Hunting Harmony for that battle round, starting with the player taking the first turn. A Hunting Harmony is made up of a number of chords. You start with 0 chords and receive 1 chord for the following:

        - If a friendly BELTHANOS is on the battlefield.
        - For each friendly EVERGREEN HUNT unit wholly ewithin the same large quarter of the battlefield as the quarry.
        - For each quarry destroyed during the battle.
        
        Add up the number of chords you received and consult the Hunting Harmony Table to see which effects will apply to your army for that battle round. These effects are cumulative: if you have 6 chords, all of the effects in the table will apply. Hunting Harmony chords are lost at the end of each battle round.
        
        0: Discordant: No effect.
        1: Simple: Add 1 to run rolls and charge rolls for friendly units wholly within the same large quarter of the battlefield as the quarry.
        2: Tuneful: Add 1 to hit rolls and wound rolls for attacks made with melee weapons that target an enemy unit wholly within the same large quarter of the battlefield as the quarry.
        3-5: Melodic: Add 1 to the Attacks characteristic of melee weapons used by friendly units while they are within 3" of the quarry.
        6+: Mellifluous: While a friendly unit is wholly within the same large quarter of the battlefield as the quarry, it is eligible to fight in the combat phase if it is within 6" of the quarry instead of 3", and it can move an extra 3" when it piles in. `,
        when: [START_OF_ROUND],
      },
      {
        name: `Adundant Growth`,
        desc: `After territories are determined, before faction terrain features are set up, you can pick up to 3 terrain features on the battlefield that are wholly outside enemy territory. These terrain features are considered by you to be overgrown terrain features.

        At the start of your hero phase, you can heal 1 wound allocated to each friendly EVERGREEN HUNT unit that is wholly within 9" of an overgrown terrain feature.`,
        when: [END_OF_SETUP],
      },
      {
        name: `Adundant Growth`,
        desc: `At the start of your hero phase, you can heal 1 wound allocated to each friendly EVERGREEN HUNT unit that is wholly within 9" of an overgrown terrain feature.`,
        when: [START_OF_HERO_PHASE],
      },
      {
        name: `Heroic Action: A Prize Quarry is Sighted`,
        desc: `Pick 1 friendly EVERGREEN HUNT HERO and 1 enemy unit within 9" of that HERO. That enemy unit becomes the quarry instead of the enemy unit that was picked to be the quarry.`,
        when: [START_OF_HERO_PHASE],
      },
    ],
  }, */
} satisfies TItemDescriptions

export default tagAs(BattleTraits, 'battle_trait')
